import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { ModalConfigI, PopupState } from "../App.types";
import { mapSelectedConfigServices } from "../utils/helpers/mapper";

import {
	getProductData,
	getSubscriptionData,
	getSubscriptionTermsData,
	setConfigService,
	removeConfigService,
	changeInsurance,
} from "./actions";
import { initialState } from "./initialState";

const initState: PopupState = initialState;

export const popupSlice = createSlice({
	name: "popup",
	initialState: initState,
	reducers: {
		setBehaviour: (state, action: PayloadAction<boolean>) => {
			state.isAddToCartDrawer = action.payload;
		},
		setIsGamePassAvailable: (state, action: PayloadAction<boolean>) => {
			state.isGamePassAvailable = action.payload;
		},
		toggleIsVisible: (state, action: PayloadAction<boolean>) => {
			state.products.isVisible = action.payload;
			if (!action.payload) {
				state.products.mainProduct.productId =
					initState.products.mainProduct.productId;
				state.products.mainProduct.subscription =
					initState.products.mainProduct.subscription;
			}
		},
		toggleModal: (
			state,
			action: PayloadAction<{
				id?: string;
				isOpen: boolean;
				config?: ModalConfigI;
			}>,
		) => {
			state.modal.isModalOpen = action.payload.isOpen;
			state.modal.id = action.payload.id;
			state.modal.config = action.payload?.config;
		},
	},
	extraReducers: {
		[getProductData.pending as any]: (state) => {
			state.isFetching = true;
		},
		[getProductData.fulfilled as any]: (state, action: PayloadAction<any>) => {
			state.products = action.payload.products;
			state.isFetching = false;

			state.singleFlexInsurance = action.payload.singleFlexInsurance;
			state.containsFlexInsurance = action.payload.containsFlexInsurance;
			state.currencyCode = action.payload.currencyCode;
			state.recommendedProductsHeader =
				action.payload.recommendedProductsHeader;
			state.canCustomerDisablePopup = action.payload.canCustomerDisablePopup;
			state.showSubscriptionDataOnly = false;
			state.features = action.payload.features;
		},
		[getProductData.rejected as any]: (state, action: PayloadAction<any>) => {
			state.isFetching = false;
			state.error = action.payload.error;
			state.showSubscriptionDataOnly = false;
		},
		[getSubscriptionData.pending as any]: (state) => {
			state.isFetching = true;
		},
		[getSubscriptionData.fulfilled as any]: (
			state,
			action: PayloadAction<any>,
		) => {
			state.products = action.payload.products;
			state.isFetching = false;
			state.showSubscriptionDataOnly = true;
		},
		[getSubscriptionData.rejected as any]: (
			state,
			action: PayloadAction<any>,
		) => {
			state.error = action.payload.error;
			state.isFetching = false;
			state.showSubscriptionDataOnly = true;
		},
		[getSubscriptionTermsData.fulfilled as any]: (
			state,
			action: PayloadAction<any>,
		) => {
			state.products.mainProduct.subscription.subscriptionTerms =
				action.payload;
		},
		[setConfigService.fulfilled as any]: (
			state,
			action: PayloadAction<any>,
		) => {
			state.products.mainProduct.configServices = mapSelectedConfigServices(
				state.products.mainProduct.configServices,
				action.payload,
			);
		},
		[removeConfigService.fulfilled as any]: (
			state,
			action: PayloadAction<any>,
		) => {
			state.products.mainProduct.configServices = mapSelectedConfigServices(
				state.products.mainProduct.configServices,
				action.payload,
			);
		},
		[changeInsurance.fulfilled as any]: (
			state,
			action: PayloadAction<{ insuranceId: string }>,
		) => {
			state.products.mainProduct.selectedInsuranceId =
				action.payload.insuranceId;
		},
	},
});

export const {
	setBehaviour,
	setIsGamePassAvailable,
	toggleIsVisible,
	toggleModal,
} = popupSlice.actions;

export default popupSlice.reducer;
